import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Select, Input, Button, Spin, Empty, Typography, Tag, Rate, Pagination } from 'antd';
import {
    SearchOutlined, EnvironmentOutlined, CloseOutlined,
} from '@ant-design/icons';
import DOMPurify from 'dompurify';
import usePublicStore from '../../store/publicStore';
import DateLine from '../../components/DateLine/DateLine';

const { Text } = Typography;
const P = '#5F33E1';

function ServiceCard({ service }) {
    const img = service.images?.[0]?.file_path
        ? process.env.REACT_APP_URL + service.images[0].file_path
        : 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&q=60';

    return (
        <Link to={`/services/${service.id}`} style={{ textDecoration: 'none' }}>
            <div className="qk-service-card">
                <div style={{ overflow: 'hidden', height: 200 }}>
                    <img className="qk-card-img" src={img} alt={service.name} />
                </div>
                <div style={{ padding: '16px 18px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--qk-text)', marginBottom: 4 }}>{service.name}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#9CA3AF', fontSize: 13 }}>
                                <EnvironmentOutlined style={{ fontSize: 12 }} />
                                {service.region?.nameUz || service.district?.nameUz || service.address || '—'}
                            </div>
                        </div>
                        {service.serviceType && (
                            <Tag color="#EEE9FF" style={{ color: P, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 11 }}>
                                {service.serviceType.name}
                            </Tag>
                        )}
                    </div>
                    {service.desc && (
                        <div
                            style={{ color: '#9CA3AF', fontSize: 13, lineHeight: 1.5, marginBottom: 8, maxHeight: 40, overflow: 'hidden' }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(service.desc.replace(/<[^>]+>/g, '').slice(0, 80) + '...') }}
                        />
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Rate disabled value={service.average_rating || 0} allowHalf style={{ fontSize: 13, color: '#FFE44D' }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>({service.total_comments || 0})</Text>
                        </div>
                        {service.min_price && (
                            <Text strong style={{ color: P, fontSize: 14 }}>
                                {Number(service.min_price).toLocaleString()} so'm
                            </Text>
                        )}
                    </div>
                </div>
                    <DateLine serviceId={service.id} />
            </div>
        </Link>
    );
}

function ServicesList() {
    const {
        services, loading, pagination, summary,
        filters, setFilter, setPage, resetFilters, fetchServices,
        regions, districts, serviceTypes,
        fetchRegions, fetchServiceTypes, fetchDistricts,
    } = usePublicStore();

    useEffect(() => {
        fetchRegions();
        fetchServiceTypes();
    }, [fetchRegions, fetchServiceTypes]);

    useEffect(() => {
        fetchServices();
    }, [filters, fetchServices]);

    const handleRegionChange = (v) => {
        setFilter('region_id', v);
        if (v) fetchDistricts(v);
        else setFilter('district_id', null);
    };

    const hasFilters = filters.region_id || filters.district_id || filters.service_type_id || filters.search;

    return (
        <div className="qk-services-list-page" style={{ maxWidth: 1280, margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ fontSize: 32, fontWeight: 800, color: 'var(--qk-text)', marginBottom: 4 }}>
                    Xizmatlar
                </h1>
                <Text type="secondary" style={{ fontSize: 15 }}>
                    {summary ? `${summary.total ?? 0} ta xizmat topildi` : 'Barcha xizmatlar'}
                </Text>
            </div>

            {/* Filters */}
            <div className="qk-filters-bar">
                <div style={{ flex: '1 1 200px' }}>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                        placeholder="Xizmat nomi bo'yicha qidirish..."
                        value={filters.search}
                        onChange={(e) => setFilter('search', e.target.value)}
                        allowClear
                        style={{ borderRadius: 10 }}
                    />
                </div>
                <div style={{ flex: '0 0 180px' }}>
                    <Select
                        placeholder="Xizmat turi"
                        value={filters.service_type_id}
                        onChange={(v) => setFilter('service_type_id', v)}
                        allowClear
                        style={{ width: '100%' }}
                        options={serviceTypes.map(s => ({ label: s.name, value: s.id }))}
                    />
                </div>
                <div style={{ flex: '0 0 180px' }}>
                    <Select
                        placeholder="Viloyat"
                        value={filters.region_id}
                        onChange={handleRegionChange}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        options={regions.map(r => ({ label: r.nameUz, value: r.id }))}
                    />
                </div>
                {filters.region_id && districts.length > 0 && (
                    <div style={{ flex: '0 0 180px' }}>
                        <Select
                            placeholder="Tuman"
                            value={filters.district_id}
                            onChange={(v) => setFilter('district_id', v)}
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }}
                            options={districts.map(d => ({ label: d.nameUz, value: d.id }))}
                        />
                    </div>
                )}
                {hasFilters && (
                    <Button
                        icon={<CloseOutlined />}
                        onClick={resetFilters}
                        style={{ borderRadius: 10 }}
                    >
                        Tozalash
                    </Button>
                )}
            </div>

            {/* Active filters tags */}
            {hasFilters && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {filters.service_type_id && (
                        <Tag closable onClose={() => setFilter('service_type_id', null)} style={{ borderRadius: 8, padding: '4px 12px' }}>
                            {serviceTypes.find(s => s.id === filters.service_type_id)?.name}
                        </Tag>
                    )}
                    {filters.region_id && (
                        <Tag closable onClose={() => handleRegionChange(null)} style={{ borderRadius: 8, padding: '4px 12px' }}>
                            {regions.find(r => r.id === filters.region_id)?.nameUz}
                        </Tag>
                    )}
                    {filters.search && (
                        <Tag closable onClose={() => setFilter('search', '')} style={{ borderRadius: 8, padding: '4px 12px' }}>
                            "{filters.search}"
                        </Tag>
                    )}
                </div>
            )}

            {/* Results */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 80 }}>
                    <Spin size="large" />
                </div>
            ) : services.length ? (
                <>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(290px, 100%), 1fr))',
                        gap: 16,
                    }}>
                        {services.map(s => <ServiceCard key={s.id} service={s} />)}
                    </div>

                    {pagination && pagination.total_pages > 1 && (
                        <div className="qk-pagination">
                            <Pagination
                                current={pagination.current_page}
                                total={pagination.total}
                                pageSize={pagination.per_page}
                                onChange={(p) => setPage(p)}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </>
            ) : (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                        <div style={{ textAlign: 'center' }}>
                            <Text type="secondary" style={{ fontSize: 16, display: 'block', marginBottom: 12 }}>
                                Hech qanday xizmat topilmadi
                            </Text>
                            {hasFilters && (
                                <Button onClick={resetFilters} style={{ borderRadius: 10 }}>
                                    Filtrlarni tozalash
                                </Button>
                            )}
                        </div>
                    }
                    style={{ padding: 80 }}
                />
            )}
        </div>
    );
}

export default ServicesList;
